import
  React
from 'react';

import
  styled
from 'styled-components';

import { Helmet } from 'react-helmet-async';

import {
  FontFamilies,
  FontSizes,
  Logger,
  MediaQueries,
  Theme,
  isValidProviderResult,
} from 'common';

import {
  Button,
  Markdown,
} from 'common/components';

import {
  Calendar,
  Pin
} from 'common/components/svg';

import {
  withCommon,
} from 'common/hocs';

import {
  EventDetailsReserveTickets
} from 'ui/event/components';

import {
  ActionContainer,
  Container,
  Content,
  DetailContainer,
  DetailContent,
  DetailContentText,
  DetailsText,
  Header,
  MainContentContainer,
  MainImage,
  MobileContentSpace,
  SideContentContainer,
  SideContent
} from 'ui/event/styled';

import {
  EventProvider,
} from 'providers';

const MobileHiddenImage = styled.img`
  width: 100%;
  height: auto;
  opacity: 0;

  @media only screen and ${MediaQueries.Md} {
    display: none;
  }
`;

const CalloutOverlay = styled.div`
  position: ${props => props.isMobileFixed ? 'fixed' : 'absolute'};
  top: ${props => props.isMobileFixed ? '85px' : '20px'};
  left: 20px;
  background: ${props => {
    const calloutText = props.children?.toLowerCase();
    if (calloutText === 'tickets available soon') {
      return Theme.EventCard.Banner.SuccessColor;
    }
    return Theme.EventCard.Banner.SoldOutColor;
  }};
  color: white;
  padding: 8px 10px;
  border-radius: 8px;
  font-size: ${FontSizes.Smaller};
  font-family: ${FontFamilies.Bold};
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  text-transform: ${props => {
    const calloutText = props.children?.toLowerCase();
    if (calloutText === 'tickets available soon') {
      return 'uppercase';
    }
    return 'none';
  }};

  @media only screen and ${MediaQueries.Md} {
    position: absolute;
    top: 20px;
    padding: 10px 12px;
    font-size: ${FontSizes.Small};
  }
`;

const ImageContainer = styled.div`
  position: relative;
  width: 100%;
`;

const EventDetailsUI = props => {

  const [state, setState] = React.useState({
    event: {}
  });

  const load = () => {

    if (!props.common?.pathParams?.id) {

      Logger.error('EventDetails', 'load', 'No eventId found');
      props.common.showToast('A technical error occurred');
      return;
    }

    const eId = typeof props.common.pathParams.id === 'string'
      ? props.common.pathParams.id.split('-')[0]
      : props.common.pathParams.id;

    EventProvider.getEventById(eId)
      .then(ret => {

        props.common.hideLoading();

        if (!isValidProviderResult(ret)) {

          Logger.error('EventDetails', 'load', `Error loading event details ${props.common.pathParams.id}: ${ret.message}`);
          props.common.navigate('/');
          return;
        }

        setState(prevState => ({
          ...prevState,
          event: ret.data
        }));
      })
      .catch(e => {

        // TODO: show error
        Logger.error('EventDetails', 'load', e);
        props.common.showToast('Oh no, we weren\'t able to load your event details, please try again.');

        props.common.hideLoading();
      });
  };

  React.useEffect(load, []); // eslint-disable-line

  const displayDateSplit = state.event.is_multi_event
    ? [state.event.displayDate]
    : (state.event.displayDate ?? '').split(' - ');

  // Generate meta description from event data
  const metaDescription = `Get your tickets for ${state.event.name || 'this event'} at ${state.event.location_display || 'the venue'}. Tickets available now.`;

  const currentUrl = window.location.href;

  return (

    <Container>
      {!props.common.isWidget && <Helmet>
          <title>{state.event.name ? `${state.event.name} - WeTicket` : 'WeTicket - Adventure awaits | Buy Tickets Online'}</title>
          <meta name="description" content={metaDescription} />
          
          {/* Open Graph tags for social media sharing */}
          <meta property="og:type" content="website" />
          <meta property="og:url" content={currentUrl} />
          <meta property="og:title" content={state.event.name ? `${state.event.name} - WeTicket` : 'WeTicket - Adventure awaits | Buy Tickets Online'} />
          <meta property="og:description" content={metaDescription} />
          {state.event.cover_media_url && <meta property="og:image" content={state.event.cover_media_url} />}
          <meta property="og:image:width" content="1200" />
          <meta property="og:image:height" content="675" />
          
          {/* Twitter Card tags */}
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content={state.event.name ? `${state.event.name} - WeTicket` : 'WeTicket - Adventure awaits | Buy Tickets Online'} />
          <meta name="twitter:description" content={metaDescription} />
          {state.event.cover_media_url && <meta name="twitter:image" content={state.event.cover_media_url} />}
          
          {/* Additional event-specific meta tags */}
          {state.event.location_display && <meta name="event:location" content={state.event.location_display} />}
          {state.event.displayDate && <meta name="event:date" content={state.event.displayDate} />}
          {state.event.priceDisplay && <meta name="event:price" content={state.event.priceDisplay} />}
        </Helmet>
      }

      <MainContentContainer boxShadow={Theme.EventDetails.ContentBoxShadow}>
 
        <ImageContainer>
          <MainImage isMobileFixed={!props.common.isWidget} src={state.event.cover_media_url}/>
          {state.event.callout && (
            <CalloutOverlay isMobileFixed={!props.common.isWidget} >
              {state.event.callout}
            </CalloutOverlay>
          )}
        </ImageContainer>

        { !props.common.isWidget && <MobileHiddenImage src={state.event.cover_media_url}/> }

        <Content
          backgroundColor={Theme.EventDetails.ContentBackground}
          desktopBackgroundColor={Theme.EventDetails.ContentBackground}>

          <Header>{state.event.name}</Header>

          <DetailContainer>

            <Pin height={25} width={22} />

            <DetailContent>
              
              <DetailContentText padding={'0 0 5px 0'} bold>{state.event.location_display}</DetailContentText>
              <DetailContentText onClick={() => window.open(state.event.map_link, '_blank')} fontSize={FontSizes.Small}>{state.event.address}</DetailContentText>
              
            </DetailContent>

          </DetailContainer>
          <DetailContainer>

            <Calendar height={25} width={22} />

            <DetailContent>
              
              <DetailContentText padding={'0 0 5px 0'} bold>{displayDateSplit[0]}</DetailContentText>
              <DetailContentText fontSize={FontSizes.Small}>{state.event.is_multi_event ? 'Time as per each event' : (state.event.displayTime || '-')}</DetailContentText>
              
            </DetailContent>

          </DetailContainer>
          <div style={{ height: '1px', width: '100%', backgroundColor: Theme.Divider.Color }} />
          <DetailsText>

            <div style={{ height: '1rem' }} />

            <Markdown markdown={state.event.description}/>

            <div style={{ height: '30px' }} />

            <MobileContentSpace
              height={'50px'}
            />

          </DetailsText>

        </Content>

        <ActionContainer>
          
          <DetailContent
            padding={'0'}>

            <DetailContentText fontSize={FontSizes.Small}>Price:</DetailContentText>
            <DetailContentText fontSize={'1.2rem'} bold>{state.event.priceDisplay}</DetailContentText>

          </DetailContent>

          <Button onClick={() => props.common.navigate(`/event/ticket/select/${state.event.redirect_id || state.event.id}`)}>Buy Tickets</Button>

        </ActionContainer>

      </MainContentContainer>

      <SideContentContainer margin={'0 15px 0 20px'}>

        <SideContent top={'0'} sticky={false}>

          <EventDetailsReserveTickets
            tickets={state.event.prices || []}
            onBuyTicketsClick={() => props.common.navigate(`/event/ticket/select/${state.event.redirect_id || state.event.id}`)}
          />

        </SideContent>

      </SideContentContainer>

    </Container>
  );
};

export const EventDetails = withCommon(EventDetailsUI, { addDefaultMetaTags: false });